const DB_NAME = 'pointCloudCache'
const STORE_NAME = 'binFiles'
const VERSION = 3

interface CacheEntry {
  hash: string
  data: ArrayBuffer
  timestamp: number
}

export class PointCloudCache {
  private db: IDBDatabase | null = null
  private readonly maxAge: number = 24 * 60 * 60 * 1000 * 7 // ttl of 7 days

  constructor() {
    this.initDB()
  }

  private async initDB(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, VERSION)

      request.onerror = (event) => {
        console.error(`Error opening IndexedDB: ${JSON.stringify(event)}`)
        reject(request.error)
      }

      request.onsuccess = () => {
        this.db = request.result
        resolve()
      }

      //this is the functiont to create the db for the specfified version with the required store etc etc
      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const store = db.createObjectStore(STORE_NAME, {
            keyPath: 'hash',
          })
          store.createIndex('timestamp', 'timestamp', { unique: false })
        }
      }
    })
  }

  private async getStore(
    mode: IDBTransactionMode = 'readonly'
  ): Promise<IDBObjectStore> {
    if (!this.db) {
      await this.initDB()
    }
    return this.db!.transaction(STORE_NAME, mode).objectStore(STORE_NAME)
  }

  async get(hash: string): Promise<ArrayBuffer | null> {
    const store = await this.getStore()
    return new Promise((resolve, reject) => {
      const request = store.get(hash)
      request.onerror = (event) => {
        console.error('Error getting from cache:', event)
        reject(request.error)
      }
      request.onsuccess = () => {
        const entry = request.result as CacheEntry | undefined
        if (!entry) {
          resolve(null)
          return
        }

        // Check if entry is expired
        if (Date.now() - entry.timestamp > this.maxAge) {
          console.error('Cache entry expired for hash:', hash)
          this.delete(hash)
          resolve(null)
          return
        }
        resolve(entry.data)
      }
    })
  }

  async set(hash: string, data: ArrayBuffer): Promise<void> {
    const store = await this.getStore('readwrite')
    const transaction = store.transaction

    return new Promise((resolve, reject) => {
      const entry: CacheEntry = {
        hash,
        data,
        timestamp: Date.now(),
      }

      const request = store.put(entry)
      request.onerror = (event) => {
        console.error('Error setting cache:', event)
        reject(request.error)
      }
      request.onsuccess = () => {
        resolve()
      }
      transaction.oncomplete = () => {
        resolve()
      }
      transaction.onerror = (event) => {
        console.error('Transaction error:', event)
        reject(transaction.error)
      }
    })
  }

  async delete(hash: string): Promise<void> {
    const store = await this.getStore('readwrite')
    const transaction = store.transaction

    return new Promise((resolve, reject) => {
      const request = store.delete(hash)
      request.onerror = (event) => {
        console.error('Error deleting from cache:', event)
        reject(request.error)
      }

      transaction.oncomplete = () => {
        resolve()
      }
      transaction.onerror = (event) => {
        console.error('Delete transaction error:', event)
        reject(transaction.error)
      }
    })
  }
}
